import type { RealtimeNotification } from './realtime.js';

function payloadText(
  payload: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  const value = payload?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function sanitizeNotificationText(
  value: string | undefined,
  preferEnglish: boolean,
): string | undefined {
  if (!value) return undefined;
  const fallback = preferEnglish ? 'Unnamed' : '未命名';
  const normalized = value
    .replace(/\bundefined\b/g, fallback)
    .replace(/\bnull\b/g, fallback)
    .replace(/\s*\(¥\?\)/g, '')
    .replace(/\s*\(\$\?\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return normalized || undefined;
}

function afterPrefix(value: string | undefined, prefix: string): string | undefined {
  if (!value?.startsWith(prefix)) return undefined;
  return value.slice(prefix.length).trim();
}

function translateLegacyTitle(
  notification: RealtimeNotification,
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const rules: Array<[string, string, (name: string) => string]> = [
    ['account_approved', '✅ 客户审核通过：', (name) => `✅ Account approved: ${name}`],
    ['account_approved', '客户审核通过：', (name) => `Account approved: ${name}`],
    ['account_rejected', '❌ 客户审核被拒：', (name) => `❌ Account rejected: ${name}`],
    ['account_rejected', '客户审核被拒：', (name) => `Account rejected: ${name}`],
    ['opportunity_approved', '✅ 商机审核通过：', (name) => `✅ Opportunity approved: ${name}`],
    ['opportunity_approved', '商机审核通过：', (name) => `Opportunity approved: ${name}`],
    ['opportunity_rejected', '❌ 商机立项被拒：', (name) => `❌ Opportunity rejected: ${name}`],
    ['opportunity_rejected', '商机立项被拒：', (name) => `Opportunity rejected: ${name}`],
    ['opportunity_won', '🎉 商机赢单：', (name) => `🎉 Opportunity won: ${name}`],
    ['opportunity_won', '商机赢单：', (name) => `Opportunity won: ${name}`],
    ['bu_announcement', '本部门新赢单：', (name) => `New team win: ${name}`],
    ['lead_approved', '✅ 线索识别通过：', (name) => `✅ Lead qualified: ${name}`],
    ['lead_approved', '线索识别通过：', (name) => `Lead qualified: ${name}`],
    ['lead_rejected', '❌ 线索识别被拒：', (name) => `❌ Lead qualification rejected: ${name}`],
    ['lead_rejected', '线索识别被拒：', (name) => `Lead qualification rejected: ${name}`],
    ['lead_approved', '✅ Lead 识别通过：', (name) => `✅ Lead qualified: ${name}`],
    ['lead_approved', 'Lead 识别通过：', (name) => `Lead qualified: ${name}`],
    ['lead_rejected', '❌ Lead 识别被拒：', (name) => `❌ Lead qualification rejected: ${name}`],
    ['lead_rejected', 'Lead 识别被拒：', (name) => `Lead qualification rejected: ${name}`],
    ['poc_approved', '✅ POC 申请通过：', (name) => `✅ POC request approved: ${name}`],
    ['poc_approved', 'POC 申请通过：', (name) => `POC request approved: ${name}`],
    ['poc_rejected', '❌ POC 申请被拒：', (name) => `❌ POC request rejected: ${name}`],
    ['poc_rejected', 'POC 申请被拒：', (name) => `POC request rejected: ${name}`],
    ['sinosure_rejected', '❌ 中信保申请被拒：', (name) => `❌ Sinosure request rejected: ${name}`],
    ['sinosure_rejected', '中信保申请被拒：', (name) => `Sinosure request rejected: ${name}`],
    ['sap_sync', '✅ SAP 同步成功：', (name) => `✅ SAP sync succeeded: ${name}`],
    ['sap_sync', 'SAP 同步成功：', (name) => `SAP sync succeeded: ${name}`],
    ['sap_sync', '❌ SAP 同步失败：', (name) => `❌ SAP sync failed: ${name}`],
    ['sap_sync', 'SAP 同步失败：', (name) => `SAP sync failed: ${name}`],
    ['sap_sync', '✅ SAP 立项成功：', (name) => `✅ SAP project created: ${name}`],
    ['sap_sync', 'SAP 立项成功：', (name) => `SAP project created: ${name}`],
    ['sap_sync', '❌ SAP 立项失败：', (name) => `❌ SAP project creation failed: ${name}`],
    ['sap_sync', 'SAP 立项失败：', (name) => `SAP project creation failed: ${name}`],
    ['approval_notify', '客户已提交审核：', (name) => `Account submitted for approval: ${name}`],
    [
      'approval_notify',
      '商机已提交立项审核：',
      (name) => `Opportunity submitted for approval: ${name}`,
    ],
    [
      'approval_notify',
      '线索已提交识别审批：',
      (name) => `Lead submitted for qualification approval: ${name}`,
    ],
    [
      'approval_notify',
      'Lead 已提交识别审批：',
      (name) => `Lead submitted for qualification approval: ${name}`,
    ],
  ];
  for (const [kind, prefix, render] of rules) {
    const name = afterPrefix(value, prefix);
    if (notification.kind === kind && name) return render(name);
  }
  return undefined;
}

function translateLegacyBody(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (value === '您被配置为仅通知人员。') {
    return 'You are configured as a notification-only recipient.';
  }
  if (value === '您提交的客户审批已通过，点击查看详情后将自动关闭 OA 审核通过通知。') {
    return 'Your account approval has passed. Opening the details will automatically close the OA approval notification.';
  }
  if (value === '您提交的客户审批已通过。') {
    return 'Your account approval has passed.';
  }
  if (value === '您提交的商机审批已通过，点击查看详情后将自动关闭 OA 审核通过通知。') {
    return 'Your opportunity approval has passed. Opening the details will automatically close the OA approval notification.';
  }
  if (value === '您提交的商机审批已通过。') {
    return 'Your opportunity approval has passed.';
  }
  if (value === '您提交的线索识别审批已通过。') {
    return 'Your lead qualification approval has passed.';
  }
  if (value === '您提交的 POC 申请审批已通过。') {
    return 'Your POC request approval has passed.';
  }
  const approvalComment = afterPrefix(value, '审批意见：') ?? afterPrefix(value, '审批人意见：');
  if (approvalComment) return `Approver comment: ${approvalComment}`;
  const reason = afterPrefix(value, '原因：');
  if (reason) return `Reason: ${reason}`;
  const projectCode = afterPrefix(value, 'SAP 项目号：');
  if (projectCode) return `SAP project code: ${projectCode}`;
  const failureReason = afterPrefix(value, '失败原因：');
  if (failureReason) return `Failure reason: ${failureReason}`;
  return undefined;
}

export function notificationDisplay(notification: RealtimeNotification, preferEnglish: boolean) {
  const preferredTitle = payloadText(notification.payload, preferEnglish ? 'titleEn' : 'titleZh');
  const preferredBody = payloadText(notification.payload, preferEnglish ? 'bodyEn' : 'bodyZh');
  const title =
    preferredTitle ??
    (preferEnglish ? translateLegacyTitle(notification, notification.title) : undefined) ??
    payloadText(notification.payload, preferEnglish ? 'titleZh' : 'titleEn') ??
    notification.title;
  const body =
    preferredBody ??
    (preferEnglish ? translateLegacyBody(notification.body) : undefined) ??
    payloadText(notification.payload, preferEnglish ? 'bodyZh' : 'bodyEn') ??
    notification.body;
  return {
    title:
      sanitizeNotificationText(title, preferEnglish) ?? (preferEnglish ? 'Notification' : '通知'),
    body: sanitizeNotificationText(body, preferEnglish),
  };
}